import type { Plan } from './plans';
import type { CouponOption } from './planCoupon';

/** A plan as Chargebee has it. Price is in dollars. */
export interface ChargebeePlanOption {
  id: string;
  name: string;
  price: string;
}

/**
 * What the pricing of a plan is derived from: the plans Chargebee has, the
 * coupons table, and whether Chargebee has answered yet.
 */
export interface PricingContext {
  options: ReadonlyArray<ChargebeePlanOption>;
  coupons: ReadonlyArray<CouponOption>;
  loaded?: boolean;
}

/**
 * The Chargebee plan a plan is billed on, which is the one with the plan's
 * own id and no other. Undefined when Chargebee has none: saving the plan
 * then creates it.
 */
export const attachedPlan = (
  plan: Plan,
  options: ReadonlyArray<ChargebeePlanOption>,
): ChargebeePlanOption | undefined => {
  const id = (plan.id ?? '').trim();
  return options.find((o) => o.id === id);
};

const AMOUNT = /^\d+(\.\d{1,2})?$/;
const amount = (v: string): number | undefined => {
  const s = (v ?? '').trim();
  return AMOUNT.test(s) && Number(s) > 0 ? Number(s) : undefined;
};

// $89, $89.50: whole dollars are written without cents.
const dollars = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** The coupon the plan applies, when the coupons table describes it. */
export const planCoupon = (plan: Plan, coupons: ReadonlyArray<CouponOption>): CouponOption | undefined => (
  plan.apply_embedded_coupon && plan.embedded_coupon
    ? coupons.find((c) => c.promo_code === plan.embedded_coupon)
    : undefined
);

/**
 * The price shown on the signup site: the RRP less the plan coupon.
 * Undefined when there is no RRP to start from.
 */
export const displayPrice = (rrp: string, coupon?: CouponOption): string | undefined => {
  const full = amount(rrp);
  if (full === undefined) return undefined;
  let price = full;
  if (coupon?.dollar_amount) price = full - coupon.dollar_amount;
  else if (coupon?.percentage) price = (full * (100 - coupon.percentage)) / 100;
  return dollars(Math.round(Math.max(price, 0) * 100) / 100);
};

/** The promo code the plan offers, when the coupons table describes it. */
export const planPromo = (plan: Plan, coupons: ReadonlyArray<CouponOption>): CouponOption | undefined => (
  plan.apply_promo_code && plan.promo_code
    ? coupons.find((c) => c.promo_code === plan.promo_code)
    : undefined
);

/**
 * The price with the promo code and the plan coupon both applied: the
 * promo comes off the RRP first, then the plan coupon off what is left,
 * which only differs from the other way round for a percentage. Empty when
 * the plan offers no promo code; undefined when it cannot be derived (no
 * RRP, or a code the coupons table does not describe).
 */
const promoPrice = (plan: Plan, coupons: ReadonlyArray<CouponOption>): string | undefined => {
  if (!plan.apply_promo_code || !plan.promo_code) return '';
  const promo = planPromo(plan, coupons);
  const coupon = planCoupon(plan, coupons);
  const applies = plan.apply_embedded_coupon && !!plan.embedded_coupon;
  if (!promo || (applies && !coupon)) return undefined;
  const afterPromo = displayPrice(plan.rrp, promo);
  return afterPromo === undefined ? undefined : displayPrice(afterPromo, coupon);
};

/**
 * The line under the price when nobody has written one: how long the
 * coupon's price lasts and the RRP that follows it.
 */
export const defaultDescription1 = (rrp: string, coupon?: CouponOption): string => {
  const full = amount(rrp);
  if (full === undefined || !coupon?.duration) return '';
  const months = coupon.duration === 1 ? 'month' : `${coupon.duration} months`;
  return `for the first ${months}, then $${dollars(full)} per month`;
};

/**
 * The plan with its derived prices: the RRP of the Chargebee plan it is
 * billed on, and the displayed price that follows from the RRP and the plan
 * coupon. What cannot be derived (Chargebee or the coupon unknown) is left
 * as stored.
 */
export const settle = (plan: Plan, ctx: PricingContext): Plan => {
  const attached = attachedPlan(plan, ctx.options);
  const rrp = attached ? attached.price : plan.rrp;
  const applies = plan.apply_embedded_coupon && !!plan.embedded_coupon;
  const coupon = planCoupon(plan, ctx.coupons);
  const cost = applies && !coupon ? undefined : displayPrice(rrp, coupon);
  // A stored price that already says this amount keeps its spelling: the
  // signup site prints it as written, "$59" or "$59.00".
  const same = cost !== undefined && plan.cost.trim() !== '' && Number(plan.cost) === Number(cost);
  const settled = { ...plan, rrp, cost: cost === undefined || same ? plan.cost : cost };
  const promo = promoPrice(settled, ctx.coupons);
  return { ...settled, promo_cost: promo ?? plan.promo_cost };
};

/**
 * A change to the plan with everything that follows from it: the derived
 * prices, and the line under the price for as long as it is still the
 * default wording. A line the user wrote is theirs.
 */
export const reprice = (before: Plan, change: Partial<Plan>, ctx: PricingContext): Plan => {
  const wasDefault = before.description1.trim() === ''
    || before.description1 === defaultDescription1(before.rrp, planCoupon(before, ctx.coupons));
  const changed = { ...before, ...change };
  // An RRP read from Chargebee belongs to that Chargebee plan: once the id
  // is no longer one Chargebee has, the RRP is the user's to enter.
  if (change.rrp === undefined && attachedPlan(before, ctx.options) && !attachedPlan(changed, ctx.options)) {
    changed.rrp = '';
  }
  const next = settle(changed, ctx);
  if (wasDefault && change.description1 === undefined) {
    next.description1 = defaultDescription1(next.rrp, planCoupon(next, ctx.coupons));
  }
  return next;
};

export const chargebeeErrors = (plan: Plan, ctx: PricingContext, isNew: boolean): string[] => {
  // Chargebee could not be read: the API settles it when the plan is saved.
  if (!ctx.loaded || attachedPlan(plan, ctx.options)) return [];
  const rrp = (plan.rrp ?? '').trim();
  if (rrp === '') {
    return isNew ? ['RRP is required: it is the price the plan is created with in Chargebee'] : [];
  }
  return amount(rrp) === undefined ? ['RRP must be a positive decimal like 89.00'] : [];
};
